import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import { ButtonBase } from '@mui/material'
import { darken } from '@mui/material/styles'
import { keyframes } from '@emotion/react'
import { useRef, useState } from 'react'
import { openMidasPanel } from 'hooks/useMidas'
import {
  LAUNCHER_HEIGHT,
  LAUNCHER_RIGHT,
  useLauncherBottom
} from './useLauncherBottom'

export const LAUNCHER_LABEL = 'Ask Goldie'

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'

// A calm first appearance: fade in and rise a little, no overshoot. Uses the
// individual translate property so the finished animation never pins
// `transform`.
const riseIn = keyframes`
  from { opacity: 0; translate: 0 12px; }
  to { opacity: 1; translate: 0 0; }
`

// The launcher remounts on every page; only animate the first time it appears.
let hasAppeared = false

export function resetLauncherAnimation(): void {
  hasAppeared = false
}

// Brand direction, 24 Sep: a simple rounded chat bubble labelled "Ask Goldie", in
// the brand pink. No mascot, image or emoji. Hover and focus change only the
// shade and the shadow. Nothing sits behind the pill: the old pink aura (a
// blurred conic gradient) painted as a hard square on iOS Safari, 22 Sep.
export default function MidasLauncher({
  hidden
}: Readonly<{ hidden: boolean }>) {
  const [animate] = useState(() => {
    const first = !hasAppeared
    hasAppeared = true
    return first
  })
  const ref = useRef<HTMLButtonElement>(null)
  const bottom = useLauncherBottom(ref)

  return (
    <ButtonBase
      ref={ref}
      aria-haspopup='dialog'
      onClick={openMidasPanel}
      disableRipple
      data-testid='midas-launcher'
      // Hidden, not unmounted, while the panel is open so the dialog can hand
      // focus back to it on close.
      style={{ bottom, visibility: hidden ? 'hidden' : 'visible' }}
      sx={{
        position: 'fixed',
        right: LAUNCHER_RIGHT,
        zIndex: theme => theme.zIndex.speedDial,
        height: LAUNCHER_HEIGHT,
        gap: 1,
        pl: 2,
        pr: 2.5,
        borderRadius: `${LAUNCHER_HEIGHT / 2}px`,
        bgcolor: 'primary.main',
        color: 'common.white',
        fontFamily: theme => theme.typography.button.fontFamily,
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxShadow: '0 2px 8px rgba(33, 30, 22, 0.18)',
        transition: 'background-color 150ms ease, box-shadow 150ms ease',
        animation: animate ? `${riseIn} 300ms ease-out both` : 'none',

        '&:hover, &.Mui-focusVisible': {
          bgcolor: theme => darken(theme.palette.primary.main, 0.12),
          boxShadow: '0 4px 14px rgba(33, 30, 22, 0.24)'
        },
        '&.Mui-focusVisible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 3
        },

        [REDUCED_MOTION]: { animation: 'none', transition: 'none' }
      }}
    >
      <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 20 }} />
      {LAUNCHER_LABEL}
    </ButtonBase>
  )
}
